import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  FaUserCircle,
  FaCrown,
  FaEdit,
  FaPlus,
  FaArrowRight,
  FaSignOutAlt,
} from "react-icons/fa";
import { getProfile, getUserModes } from "../../api/user";
import { getXPProgress, levelTitles } from "../../utils/leveling";
import PublishedModeCard from "../cards/PublishedModeCard";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { useAuth } from "../../contexts/AuthContext";
import SubscriptionModal from "../subscription/SubscriptionModal";
import { Button, Card, PageSpinner, Spinner } from "../../ui";

const Stat = ({ value, label }) => (
  <div className="flex-1 text-center">
    <div className="text-white text-xl font-extrabold">{value ?? 0}</div>
    <div className="text-white/55 text-xs mt-0.5">{label}</div>
  </div>
);

// Normalise the various possible shapes of the profile-modes response.
const pageModes = (p) =>
  Array.isArray(p) ? p : p?.data || p?.modes || [];

// Shared profile body for own (حسابي) and other-user profiles.
const ProfileView = ({ userId, isOwn }) => {
  const navigate = useNavigate();
  const { isFreeTier, isLimitReached, showUpgradeModal, isPremium } =
    useSubscription();
  const { logout } = useAuth();
  const [subOpen, setSubOpen] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["userProfile", userId],
    queryFn: () => getProfile(userId),
    enabled: !!userId,
  });

  const {
    data: modesData,
    isLoading: modesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["profileModes", userId],
    queryFn: ({ pageParam = 1 }) => getUserModes(userId, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last, all) =>
      pageModes(last).length >= 12 ? all.length + 1 : undefined,
    enabled: !!userId,
  });

  if (isLoading || !userId) {
    return <PageSpinner />;
  }
  if (!profile) {
    return <p className="text-white/70 text-center py-20">المستخدم غير موجود</p>;
  }

  const name = profile.gameName || profile.name || "";
  const xp = profile.xp || 0;
  const { progress, level } = getXPProgress(xp);
  const rank = profile.specialBadges?.[0]?.text || levelTitles[level] || "";
  // Gold ring only for users with a badge (premium); faint otherwise — like the app.
  const badgeColor = profile.specialBadges?.[0]?.color || "#647AAA";
  const stats = profile.gameStats;
  const modes = (modesData?.pages || []).flatMap(pageModes);

  const handleCreate = () => {
    const owned = profile.customModesCount ?? modes.length;
    if (isFreeTier && isLimitReached("MAX_CUSTOM_MODES", owned)) {
      showUpgradeModal({ messageKey: "CUSTOM_MODES_LIMIT" });
      return;
    }
    navigate("/app/modes/create");
  };

  return (
    <div className="max-w-3xl mx-auto">
      {!isOwn && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 w-10 h-10 rounded-xl border border-white/10 bg-white/[0.05] text-white/70 hover:text-white hover:bg-white/10 flex items-center justify-center"
          aria-label="رجوع"
        >
          <FaArrowRight aria-hidden="true" />
        </button>
      )}

      <div className="flex flex-col items-center text-center mb-6">
        {profile.image ? (
          <img
            src={profile.image}
            alt=""
            className="w-24 h-24 rounded-full object-cover border-2"
            style={{ borderColor: badgeColor }}
          />
        ) : (
          <FaUserCircle className="w-24 h-24 text-white/30" />
        )}
        <div className="flex items-center gap-2 mt-3">
          <h1 className="text-2xl font-extrabold">{name}</h1>
          {(isOwn ? isPremium : profile.isPremium) && (
            <FaCrown className="text-primary" aria-label="مشترك مميز" />
          )}
        </div>
        {rank && <p className="text-primary mt-1 font-semibold">{rank}</p>}
        {isOwn && (
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {isPremium ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSubOpen(true)}
                icon={<FaCrown className="text-primary" aria-hidden="true" />}
              >
                اشتراكي
              </Button>
            ) : (
              <Button
                variant="gold"
                size="sm"
                onClick={() => setSubOpen(true)}
                icon={<FaCrown aria-hidden="true" />}
              >
                اشترك
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => toast.info("تعديل الملف الشخصي قريباً على الويب")}
              icon={<FaEdit aria-hidden="true" />}
            >
              تعديل الملف الشخصي
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              icon={<FaSignOutAlt aria-hidden="true" />}
            >
              خروج
            </Button>
          </div>
        )}
      </div>

      <Card pad="sm" className="mb-4 flex items-center">
        <Stat value={profile.totalLikes} label="الإعجابات" />
        <div className="w-px h-8 bg-white/10" />
        <Stat value={profile.followersCount} label="متابعون" />
        <div className="w-px h-8 bg-white/10" />
        <Stat value={profile.customModesCount} label="فئات" />
      </Card>

      {stats && (
        <Card pad="sm" className="mb-4">
          <div className="flex justify-between mb-3">
            <span className="font-bold">إحصائياتك ضده</span>
            <span className="text-white/55 text-sm">
              المجموع: {stats.totalGames || 0}
            </span>
          </div>
          <div className="flex rounded-lg overflow-hidden h-8 text-sm font-bold">
            <div className="bg-success text-white flex-1 flex items-center justify-center">
              {stats.visitorWins || 0}
            </div>
            <div className="bg-white/15 text-white flex-1 flex items-center justify-center">
              {stats.draws || 0}
            </div>
            <div className="bg-danger text-white flex-1 flex items-center justify-center">
              {stats.profileUserWins || 0}
            </div>
          </div>
        </Card>
      )}

      <div className="mb-6 px-1">
        <div className="flex justify-between text-sm mb-1.5">
          <span className="text-primary font-semibold">المستوى {level}</span>
          <span className="text-white/55" dir="ltr">
            {xp} XP
          </span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>

      {isOwn && (
        <Button
          block
          size="lg"
          className="mb-8"
          onClick={handleCreate}
          icon={<FaPlus className="text-base" aria-hidden="true" />}
        >
          إنشاء فئة جديدة
        </Button>
      )}

      <h2 className="text-lg font-extrabold mb-3">الفئات</h2>
      {modesLoading ? (
        <div className="flex justify-center py-8 text-white/80">
          <Spinner size={24} />
        </div>
      ) : modes.length === 0 ? (
        <Card pad="md" className="text-center text-white/55">
          لا توجد فئات
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {modes.map((m) => (
              <PublishedModeCard key={m._id} mode={m} inProfile />
            ))}
          </div>
          {hasNextPage && (
            <div className="flex justify-center mt-6">
              <Button
                variant="secondary"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                loading={isFetchingNextPage}
              >
                عرض المزيد
              </Button>
            </div>
          )}
        </>
      )}

      <SubscriptionModal open={subOpen} onClose={() => setSubOpen(false)} />
    </div>
  );
};

export default ProfileView;
